<template>
    <div>
        <Modal :show="showModal" @close="closeModal" label="$PageEditor$:editor">
            <div v-if="loadingPages" class="text-center py-4">
                <Translate value="$PageEditor$:loading-pages" />
            </div>
            <div v-else class="p-4">
                <!-- Root Drop Zone -->
                <div 
                    class="mb-4 p-2 border-2 border-dashed border-gray-300 rounded text-center text-gray-500 text-sm"
                    :class="{ 'border-blue-500 bg-blue-50': dropZoneActive === 'root' }"
                    @dragover="onDragOver($event, { id: 'root' })"
                    @dragleave="onDragLeave"
                    @drop="onDropAsRoot">
                    <Translate value="$PageEditor$:drop-root" />
                </div>
                
                <!-- Action Buttons -->
                <div v-if="hasUnsavedChanges" class="mb-4 flex gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded">
                    <button 
                        class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                        @click="applyAllChanges"
                        :disabled="loadingPages">
                        <Translate value="$PageEditor$:apply-changes" :count="pendingCommands.length" />
                    </button>
                    <button 
                        class="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                        @click="discardChanges"
                        :disabled="loadingPages">
                        <Translate value="$PageEditor$:discard-changes" />
                    </button>
                </div>
                
                <div v-for="rootPage in formattedPageTree" :key="rootPage.id" class="mb-4 relative">
                    <!-- Root Page -->
                    <div 
                        class="font-bold text-lg mb-2 flex items-center p-2 rounded cursor-move select-none relative z-10"
                        :class="{
                            'bg-blue-50 border-2 border-blue-500': dropZoneActive === rootPage.id && dropAction === 'parent'
                        }"
                        draggable="true"
                        @dragstart="onDragStart($event, rootPage)"
                        @dragend="onDragEnd"
                        @dragover="onDragOver($event, rootPage)"
                        @dragleave="onDragLeave"
                        @drop="onDrop($event, rootPage)">
                        <span class="drag-handle mr-2 cursor-move text-gray-400 hover:text-gray-600" title="Drag to move">⋮⋮</span>
                        <FolderIcon class="w-5 h-5 mr-2 text-blue-600 pointer-events-none" />
                        <span class="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mr-2 pointer-events-none">{{ rootPage.id }}</span>
                        <Translate :value="rootPage.title" class="pointer-events-none" />
                        <span class="text-sm text-gray-500 ml-2 pointer-events-none">({{ rootPage.route }})</span>
                        <LanguageIcon class="w-4 h-4 ml-2 text-blue-500 cursor-pointer pointer-events-auto hover:text-blue-700" title="View Translations" @click.stop="openTranslationModal(rootPage.title)" />
                        <StarIcon v-if="isCurrentPage(rootPage)" class="size-5 ml-2 text-yellow-400 cursor-pointer pointer-events-auto" />
                        <button 
                            class="ml-2 w-6 h-6 bg-blue-500 hover:bg-blue-600 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer pointer-events-auto" 
                            @click.stop="openEditPageModal(rootPage)"
                            title="Edit Page">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                            </svg>
                        </button>
                        <button 
                            class="ml-2 w-6 h-6 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer pointer-events-auto" 
                            @click.stop="openAddPageModal(rootPage.id)"
                            title="Add Child Page">
                            <PlusIcon class="w-4 h-4" />
                        </button>
                    </div>
                    
                    <!-- Children -->
                    <div v-if="rootPage.children.length > 0" class="ml-6">
                        <div v-for="child in rootPage.children" :key="child.id" class="mb-2 border-l-2 border-gray-200 pl-4 relative">
                            <div 
                                class="font-medium flex items-center p-2 rounded cursor-move select-none relative z-10"
                                :class="{
                                    'bg-green-50 border-2 border-green-500': dropZoneActive === child.id && dropAction === 'parent'
                                }"
                                draggable="true"
                                @dragstart="onDragStart($event, child)"
                                @dragend="onDragEnd"
                                @dragover="onDragOver($event, child)"
                                @dragleave="onDragLeave"
                                @drop="onDrop($event, child)">
                                <span class="drag-handle mr-2 cursor-move text-gray-400 hover:text-gray-600" title="Drag to move">⋮⋮</span>
                                <DocumentIcon class="w-4 h-4 mr-2 text-green-600 pointer-events-none" />
                                <span class="bg-green-100 text-green-800 text-xs px-2 py-1 rounded mr-2 pointer-events-none">{{ child.id }}</span>
                                <Translate :value="child.title" class="pointer-events-none" />
                                <span class="text-sm text-gray-500 ml-2 pointer-events-none">({{ child.route }})</span>
                                <LanguageIcon class="w-3 h-3 ml-2 text-blue-500 cursor-pointer pointer-events-auto hover:text-blue-700" title="View Translations" @click.stop="openTranslationModal(child.title)" />
                                <StarIcon v-if="isCurrentPage(child)" class="size-4 ml-2 text-yellow-400 cursor-pointer pointer-events-auto" />
                                <button 
                                    class="ml-2 w-5 h-5 bg-blue-500 hover:bg-blue-600 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer pointer-events-auto" 
                                    @click.stop="openEditPageModal(child)"
                                    title="Edit Page">
                                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                                    </svg>
                                </button>
                                <button 
                                    class="ml-2 w-5 h-5 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer pointer-events-auto" 
                                    @click.stop="openAddPageModal(child.id)"
                                    title="Add Child Page">
                                    <PlusIcon class="w-3 h-3" />
                                </button>
                            </div>
                            
                            <!-- Grandchildren -->
                            <div v-if="child.children.length > 0" class="ml-6 mt-1">
                                <div v-for="grandchild in child.children" :key="grandchild.id" class="mb-1 relative">
                                    <div class="text-sm text-gray-600 flex items-center p-1 rounded cursor-move select-none relative z-10"
                                        :class="{
                                            'bg-purple-50 border-2 border-purple-500': dropZoneActive === grandchild.id && dropAction === 'parent'
                                        }"
                                        draggable="true"
                                        @dragstart="onDragStart($event, grandchild)"
                                        @dragend="onDragEnd"
                                        @dragover="onDragOver($event, grandchild)"
                                        @dragleave="onDragLeave"
                                        @drop="onDrop($event, grandchild)">
                                        <span class="drag-handle mr-2 cursor-move text-gray-400 hover:text-gray-600" title="Drag to move">⋮⋮</span>
                                        <ClipboardDocumentListIcon class="w-3 h-3 mr-2 text-purple-600 pointer-events-none" />
                                        <span class="bg-purple-100 text-purple-800 text-xs px-1.5 py-0.5 rounded mr-2 pointer-events-none">{{ grandchild.id }}</span>
                                        <Translate :value="grandchild.title" class="pointer-events-none" />
                                        <span class="text-xs text-gray-400 ml-2 pointer-events-none">({{ grandchild.route }})</span>
                                        <LanguageIcon class="w-3 h-3 ml-2 text-blue-500 cursor-pointer pointer-events-auto hover:text-blue-700" title="View Translations" @click.stop="openTranslationModal(grandchild.title)" />
                                        <StarIcon v-if="isCurrentPage(grandchild)" class="size-4 ml-2 text-yellow-400 cursor-pointer pointer-events-auto" />
                                        <button 
                                            class="ml-2 w-4 h-4 bg-blue-500 hover:bg-blue-600 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer pointer-events-auto" 
                                            @click.stop="openEditPageModal(grandchild)"
                                            title="Edit Page">
                                            <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="mt-4 flex items-center">
                    <span class="text-sm text-gray-600 mr-3">Add new root page:</span>
                    <button 
                        class="w-8 h-8 bg-blue-500 hover:bg-blue-600 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer" 
                        @click.stop="openAddPageModal()"
                        title="Add Root Page">
                        <PlusIcon class="w-5 h-5" />
                    </button>
                </div>
            </div>
        </Modal>
        
        <!-- Translation Modal -->
        <TranslationModal 
            :show="showTranslationModal" 
            :current-translation-value="currentTranslationValue"
            @close="closeTranslationModal"
            @translations-saved="onTranslationsSaved"
        />
        
        <!-- Add Page Modal -->
        <AddPageModal 
            :show="showAddPageModal" 
            :parent-id="addPageParentId"
            :pages="pages"
            :roles="roles"
            @close="closeAddPageModal"
            @page-added="onPageAdded"
        />
        
        <!-- Edit Page Modal -->
        <EditPageModal 
            :show="showEditPageModal" 
            :page-data="editPageData"
            :roles="roles"
            :PageDisplayTypes="PageDisplayTypes"
            @close="closeEditPageModal"
            @page-updated="onPageUpdated"
        />
    </div>
</template>

<script>
import Modal from '@/components/Blocks/Modal.vue'
import TranslationModal from '@/Admin/PageEditor/TranslationModal.vue'
import AddPageModal from '@/Admin/PageEditor/AddPageModal.vue'
import EditPageModal from '@/Admin/PageEditor/EditPageModal.vue'
import { 
    LanguageIcon, 
    PlusIcon
} from '@heroicons/vue/24/outline';
import FetchData from '@/utils/FetchData';
export default {
    name: 'PageEditor',
    emits: ['modal-closed'],
    components: {
        Modal,
        TranslationModal,
        AddPageModal,
        EditPageModal,
        LanguageIcon,
        PlusIcon
    },
    data() {
        return {
            showModal: false,
            pages: [],
            pageTree: [],
            loadingPages: false,
            draggedPage: null,
            dropZoneActive: null,
            dropAction: null, // Only 'parent' now
            pendingCommands: [], // Array to store pending changes
            hasUnsavedChanges: false, // Track if there are unsaved changes
            originalValues: {}, // Track original values when modal opens
            showTranslationModal: false,
            currentTranslationValue: '',
            showAddPageModal: false,
            addPageParentId: null,
            roles: [], // Available roles fetched from server
            showEditPageModal: false,
            editPageData: null,
            PageDisplayTypes: []
        }
    },
    computed: {
        formattedPageTree() {
            return this.buildPageTree(this.pages);
        }
    },
    methods: {
        async LoadPageDisplayTypes() {
            const route = "LoadPagesDisplayTypes";
            try {
                const response = await FetchData({}, route, "POST", true);
                this.PageDisplayTypes = response;
            } catch (error) {
                console.error("Error fetching page display types:", error);
                this.PageDisplayTypes = [];
            }
        },
        async getRoles() {
            let route = "GetRoles";
            let method = "POST";
            let config = {
                select: [
                    "id", 
                    "name"
                ]
            };
            try{
                let response = await FetchData(config, route, method, true);
                this.roles = response;
            } catch (error) {
                console.error("Error fetching roles:", error);
            }
        },
        closeModal() {
            this.showModal = false;
            this.$emit('modal-closed');
        },
        isCurrentPage(page) {
            // Only match if the page route has parameters (contains :) AND current route has actual parameter values
            if (!page.route.includes(':')) {
                // Page route has no parameters, only match exact routes
                return this.$route.path === page.route;
            }
            
            // Page route has parameters - check if current route matches the pattern
            const currentRouteBase = this.$route.path.replace(/\/\d+$/, '');
            const pageRouteBase = page.route.replace(/\/:\w+/g, '').replace(/\/\d+$/, '');
            
            // Only match if current route actually has parameters (is longer than base)
            const hasParameters = this.$route.path !== currentRouteBase;
            
            return hasParameters && currentRouteBase === pageRouteBase;
        },
        buildPageTree(pages) {
            // Create a map for quick lookup
            const pageMap = {};
            pages.forEach(page => {
                pageMap[page.id] = { ...page, children: [] };
            });

            // Separate root pages and organize children
            const rootPages = [];
            pages.forEach(page => {
                if (page.parent === null) {
                    rootPages.push(pageMap[page.id]);
                } else {
                    if (pageMap[page.parent]) {
                        pageMap[page.parent].children.push(pageMap[page.id]);
                    }
                }
            });

            return rootPages;
        },
        async FetchPages(){
            this.loadingPages = true;
            const route = "PostPages";
            try{
                const response = await FetchData({}, route, "POST", true);
                this.pages = response;
                this.pageTree = this.buildPageTree(response);
                
                // Store original values for change tracking
                this.storeOriginalValues();
            } catch (error) {
                console.error("Error fetching pages:", error);
                this.pages = [];
                this.pageTree = [];
            } finally {
                this.loadingPages = false;
            }
        },
        async changeParent(pageId, newParentId) {
            
            // Find the page to get current parent
            const page = this.pages.find(p => p.id === pageId);
            if (!page) {
                console.warn('Page not found:', pageId);
                return;
            }
            
            // Check if the parent is actually changing
            if (page.parent === newParentId) {
                return;
            }
            
            // Apply change locally first
            this.applyParentChangeLocally(pageId, newParentId);
            
            // Check if this change differs from original value
            const originalValue = this.originalValues[pageId]?.parent;
            if (originalValue === newParentId) {
                // Value is back to original, remove any existing parent commands for this page
                this.removeCommand('parent', pageId);
                return;
            }
            
            // Remove any existing parent command for this page before adding new one
            this.removeCommand('parent', pageId);
            
            // Add command to pending list
            const command = {
                type: 'parent',
                pageId: pageId,
                originalValue: originalValue,
                newValue: newParentId
            };
            this.addCommand(command);
        },
        addCommand(command) {
            this.pendingCommands.push(command);
            this.hasUnsavedChanges = true;
        },
        removeCommand(type, pageId) {
            // Remove any existing command of the same type for the same page
            this.pendingCommands = this.pendingCommands.filter(cmd => 
                !(cmd.type === type && cmd.pageId === pageId)
            );
            
            // Update unsaved changes status
            this.hasUnsavedChanges = this.pendingCommands.length > 0;
        },
        storeOriginalValues() {
            // Store original values for all pages
            this.originalValues = {};
            this.pages.forEach(page => {
                this.originalValues[page.id] = {
                    parent: page.parent
                };
            });
        },
        applyParentChangeLocally(pageId, newParentId) {
            // Find and update the page in the local pages array
            const page = this.pages.find(p => p.id === pageId);
            if (page) {
                page.parent = newParentId;
            }
        },
        async applyAllChanges() {
            if (this.pendingCommands.length === 0) return;
            
            this.loadingPages = true;
            
            try {
                // Group commands by pageId
                const groupedCommands = this.groupCommandsByPageId(this.pendingCommands);
                
                // Process each page's changes as a single request
                for (const [pageId, commands] of Object.entries(groupedCommands)) {
                    await this.executeGroupedCommands(pageId, commands);
                }
                
                // Clear pending commands
                this.pendingCommands = [];
                this.hasUnsavedChanges = false;
                
                // Refresh from server to ensure consistency
                await this.FetchPages();
                
            } catch (error) {
                console.error("Error applying changes:", error);
                // On error, refresh from server to restore original state
                await this.FetchPages();
            } finally {
                this.loadingPages = false;
            }
        },
        groupCommandsByPageId(commands) {
            const grouped = {};
            
            commands.forEach(command => {
                if (!grouped[command.pageId]) {
                    grouped[command.pageId] = [];
                }
                grouped[command.pageId].push(command);
            });
            
            return grouped;
        },
        async executeGroupedCommands(pageId, commands) {
            const route = "PostPages/" + pageId;
            const config = {};
            
            // Build the config object with all changes for this page
            commands.forEach(command => {
                switch (command.type) {
                    case 'parent':
                        config.parent = command.newValue;
                        break;
                    // Add more cases here if you add other change types in the future
                }
            });
            
            // Send all changes for this page in a single request
            await FetchData(config, route, "PUT");
        },
        discardChanges() {
            // Restore original values to local state
            this.pages.forEach(page => {
                const original = this.originalValues[page.id];
                if (original) {
                    page.parent = original.parent;
                }
            });
            
            // Clear pending commands and refresh from server
            this.pendingCommands = [];
            this.hasUnsavedChanges = false;
            this.FetchPages();
        },
        onDragStart(event, page) {
            this.draggedPage = page;
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', page.id.toString());
            
            // Clear any existing states
            this.dropAction = null;
            this.dropZoneActive = null;
            
            // Set up visual feedback for the dragged element
            const element = event.currentTarget;
            
            // Use setTimeout to apply visual changes after drag starts
            setTimeout(() => {
                element.style.opacity = '0.6';
                element.classList.add('dragging');
            }, 10);
        },
        onDragEnd(event) {
            // Reset visual feedback immediately
            const element = event.currentTarget;
            if (element) {
                element.style.opacity = '1';
                element.classList.remove('dragging');
            }
            
            // Clean up all drag-related state
            setTimeout(() => {
                this.draggedPage = null;
                this.dropZoneActive = null;
                this.dropAction = null;
            }, 100);
        },
        onDragOver(event, targetPage) {
            if (!this.draggedPage || !targetPage) return;
            
            // Prevent default to allow drop
            event.preventDefault();
            
            // Check if we can drop (change parent)
            const canDrop = this.canDropOn(targetPage);
            
            if (canDrop) {
                event.dataTransfer.dropEffect = 'move';
                this.dropZoneActive = targetPage.id;
                this.dropAction = 'parent';
            } else {
                event.dataTransfer.dropEffect = 'none';
            }
        },
        onDragLeave() {
            // Simple drag leave - just clear if we're actually leaving
            setTimeout(() => {
                if (!this.draggedPage) return;
                this.dropZoneActive = null;
                this.dropAction = null;
            }, 50);
        },
        onDrop(event, targetPage) {
            event.preventDefault();
            
            if (!this.draggedPage || !targetPage) return;
            
            // Only handle parent change
            if (this.canDropOn(targetPage)) {
                const newParentId = targetPage.id;
                this.changeParent(this.draggedPage.id, newParentId);
            }
            
            this.dropZoneActive = null;
            this.dropAction = null;
        },
        onDropAsRoot(event) {
            event.preventDefault();
            if (this.draggedPage) {
                this.changeParent(this.draggedPage.id, null);
            }
            this.dropZoneActive = null;
            this.dropAction = null;
        },
        canDropOn(targetPage) {
            if (!this.draggedPage || !targetPage) return false;
            
            // Can't drop on itself
            if (this.draggedPage.id === targetPage.id) return false;
            
            // Can't drop on its own children (would create circular reference)
            if (this.isDescendantOf(targetPage, this.draggedPage)) return false;
            
            // Allow dropping on any page that's not a descendant
            return true;
        },
        isDescendantOf(potentialDescendant, ancestor) {
            // Check if potentialDescendant is a child of ancestor
            const findInChildren = (page, searchId) => {
                if (page.id === searchId) return true;
                return page.children && page.children.some(child => findInChildren(child, searchId));
            };
            
            return ancestor.children && ancestor.children.some(child => findInChildren(child, potentialDescendant.id));
        },
        
        // Translation Modal Event Handlers
        openTranslationModal(translationValue) {
            this.currentTranslationValue = translationValue;
            this.showTranslationModal = true;
        },
        closeTranslationModal() {
            this.showTranslationModal = false;
            this.currentTranslationValue = '';
        },
        onTranslationsSaved() {
            // Refetch pages to update the interface
            this.FetchPages();
        },
        
        // Add Page Modal Event Handlers
        openAddPageModal(parentId = null) {
            this.addPageParentId = parentId;
            this.showAddPageModal = true;
        },
        closeAddPageModal() {
            this.showAddPageModal = false;
            this.addPageParentId = null;
        },
        onPageAdded() {
            // Refresh the pages list to show the new page
            this.FetchPages();
        },
        
        // Edit Page Modal Event Handlers
        openEditPageModal(pageData) {
            this.editPageData = { ...pageData };
            this.showEditPageModal = true;
        },
        closeEditPageModal() {
            this.showEditPageModal = false;
            this.editPageData = null;
        },
        onPageUpdated() {
            // Refresh the pages list to show the updated page
            this.FetchPages();
        }
    },
    watch: {
        showModal(newValue) {
            if (newValue) {
                this.FetchPages(); 
                this.getRoles();
                this.LoadPageDisplayTypes();
            } else {
                // Clear original values when modal closes
                this.originalValues = {};
                this.pendingCommands = [];
                this.hasUnsavedChanges = false;
            }
        }
    }
};
</script>